import { Inject } from '@odinjs/odin';

import { PanelAPI, RadioGroupAPI, RadioGroupItem } from '@philips-internal/oswald-ui-components/lib/components';
import Controller, { OnClick, OnClose, OnMount } from '@philips-internal/oswald-ui-core/lib/client';
import { EventData } from '@philips-internal/oswald-ui-core/lib/component/event';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { ModalService } from '@philips-internal/oswald-ui-components';
import { ToastLevel } from '@philips-internal/oswald-ui-constants';

import { OpenConsentModalService, STATUS_OF_CONSENT } from './open-consent-modal-service';
import { ClinicalNotesConsentAliases, FragmentNames } from '../../../codegen/index';
import { ClinicalNotesConsentsRequest } from '../clinical-notes-types';
import SmartCareService from '../../../services/pep/smartcare-service';
import { ApplicationRoute } from '../../../constants/routes';
import ShowToast from '../../../utils/toast-service';
import BaseController from '../../BaseController';
import { FoundationUtils } from '../../../utils/foundation-utils';
import { DATA_STRUCTURING_99667Parameters, FunctionName } from '../../../utils/parameters';
import { BooleanUtils } from '../../../utils/boolean-utils';
import SmartcareMetricxTags from './smartcare/smartcare-metricx-tags';
import { IAMetricxTags } from '../../../constants/enums/ia-metricx-tags';

enum TYPE_OF_CONSENT {
  RECORD_TRANSCRIBE_CONSENT_AUDIO = '1',
  RECORD_VERBAL_CONSENT = '2'
}

@Controller({ component: ClinicalNotesConsentAliases.LAYOUT_CLINICAL_NOTES_CONSENT, fragment: FragmentNames.CLINICAL_NOTES_CONSENT })
export default class ClinicalNotesConsentController extends BaseController {

  @Inject(ClinicalNotesConsentAliases.CLINICAL_NOTES_OPTIONS_MODAL_PANEL)
  private clinicalNotesOptionsModalPanelApi!: PanelAPI;

  @Inject(ClinicalNotesConsentAliases.PANEL_COLLECT_CONSENT)
  private panelCollectConsentApi!: PanelAPI;

  @Inject(ClinicalNotesConsentAliases.RADIO_GROUP_COLLECT_CONSENT)
  private radioGroupCollectConsentApi!: RadioGroupAPI;

  @Inject(ClinicalNotesConsentAliases.PANEL_REFUSAL_MODAL)
  private panelModalRefusalApi!: PanelAPI;

  @Inject(ClinicalNotesConsentAliases.PANEL_CONFIRM_REFUSAL)
  private panelConfirmRefusalApi!: PanelAPI;

  @Inject(ClinicalNotesConsentAliases.PANEL_CONSENT_WITHOUT_RECORDING)
  private panelConsentWithoutRecordingApi!: PanelAPI;

  @OnMount(ClinicalNotesConsentAliases.LAYOUT_CLINICAL_NOTES_CONSENT)
  handleMount(): void {
    SmartCareService
      .isAvailable()
      .then((response) => {
        if (!response.isAvailable) {
          this.closeMainModal()
          applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_CREATE);
          return;
        }
        SmartcareMetricxTags.createAllTags(IAMetricxTags.CONSULTATION_RECORD_FEATURE);
        this.clinicalNotesOptionsModalPanelApi.setVisible(true);
        this.context.set({ smartCareUrl: response.url });
      })
  }

  private closeMainModal(): void {
    ModalService.close(FragmentNames.CLINICAL_NOTES_CONSENT)
  }

  @OnClose([
    ClinicalNotesConsentAliases.CLINICAL_NOTES_MODAL,
    ClinicalNotesConsentAliases.MODAL_COLLECT_CONSENT,
    ClinicalNotesConsentAliases.MODAL_REFUSAL,
    ClinicalNotesConsentAliases.MODAL_CONFIRM_REFUSAL,
    ClinicalNotesConsentAliases.MODAL_CONSENT_WITHOUT_RECORDING
  ])
  handleCloseModal(): void {
    this.closeMainModal()
  }

  @OnClick([
    ClinicalNotesConsentAliases.CANCEL_REFULSA_BUTTON,
    ClinicalNotesConsentAliases.BUTTON_CANCEL_CONFIRM_REFUSAL,
    ClinicalNotesConsentAliases.BUTTON_CANCEL_CONSENT_WITHOUT_RECORDING,
  ])
  handleCancelRefusalClick(): void {
    this.closeMainModal()
  }

  @OnClick(ClinicalNotesConsentAliases.WRITE_OPTION)
  handleWriteOptionClick(): void {
    this.closeMainModal()
    applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_CREATE);
  }

  @OnClick(ClinicalNotesConsentAliases.RECORD_OPTION)
  handleRecordOptionClick(): void {
    this.clinicalNotesOptionsModalPanelApi.setVisible(false);
    const generateConsentWithouAudioRecording = FoundationUtils
      .findModuleParam(FunctionName.DATA_STRUCTURING_99667, DATA_STRUCTURING_99667Parameters.ALLOW_GENERATED_CONSENT_WITHOUT_AUDIO_RECORDING) as string;

    if (BooleanUtils.isTrueValue(generateConsentWithouAudioRecording)) {
      this.panelConsentWithoutRecordingApi.setVisible(true);
      return
    }
    this.panelCollectConsentApi.setVisible(true);
  }

  @OnMount(ClinicalNotesConsentAliases.RADIO_GROUP_COLLECT_CONSENT)
  handleRadioGroupRecordTypeMount({ target }: EventData<RadioGroupAPI>): void {
    const data = [
      {
        key: TYPE_OF_CONSENT.RECORD_TRANSCRIBE_CONSENT_AUDIO,
        label: MessageService.translate('text.record-transcribe-consent-audio'),
        value: TYPE_OF_CONSENT.RECORD_TRANSCRIBE_CONSENT_AUDIO
      },
      {
        key: TYPE_OF_CONSENT.RECORD_VERBAL_CONSENT,
        label: MessageService.translate('text.record-consent-with-confirmation-without-audio'),
        value: TYPE_OF_CONSENT.RECORD_VERBAL_CONSENT
      }
    ] as RadioGroupItem[]
    target.setData(data);
    target.setValue(data[0]);
  }

  @OnClick(ClinicalNotesConsentAliases.CONTINUE_BUTTON)
  handleContinueClick(): void {
    const selectRecordType = this.radioGroupCollectConsentApi.getValue()?.value;
    this.panelCollectConsentApi.setVisible(false);
    if (selectRecordType === TYPE_OF_CONSENT.RECORD_TRANSCRIBE_CONSENT_AUDIO) {
      this.closeMainModal()
      const smartCareUrl = this.context.get('smartCareUrl') as string
      applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_RECORDING_OF_CONSENT, {}, { state: { smartCareUrl } });
      return
    }
    this.panelConfirmRefusalApi.setVisible(true);
  }

  @OnClick(ClinicalNotesConsentAliases.REGISTER_REFUSAL_BUTTON)
  handleRegisterRefusalClick(): void {
    this.panelCollectConsentApi.setVisible(false);
    this.panelModalRefusalApi.setVisible(true);
  }

  @OnClick(ClinicalNotesConsentAliases.CONFIRM_REFUSAL_BUTTON)
  handleConfirmRefusalClick(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const body = {
      encounterId,
      patientId,
      statusConsent: STATUS_OF_CONSENT.REFUSED,
      transcription: ''
    }

    OpenConsentModalService.generateConsent(body as ClinicalNotesConsentsRequest)
      .then(() => {
        ShowToast.open(MessageService.translate('text.refusal-registered'), ToastLevel.SUCCESS);
        this.closeMainModal()
      });
  }

  @OnClick([
    ClinicalNotesConsentAliases.BUTTON_CONFIRM_REFUSAL,
    ClinicalNotesConsentAliases.BUTTON_CONFIRM_CONSENT_WITHOUT_RECORDING
  ])
  handleConfirmRefusalModalClick(): void {
    const { patientId, encounterId } = this.historyService.getCurrentRouteParams();
    const body = {
      encounterId,
      patientId,
      statusConsent: STATUS_OF_CONSENT.ACCEPTED,
      transcription: ''
    }

    OpenConsentModalService.generateConsent(body as ClinicalNotesConsentsRequest)
      .then(() => {
        this.panelConfirmRefusalApi.setVisible(false);
        this.closeMainModal()
        ShowToast.open(MessageService.translate('text.consent-registered'), ToastLevel.SUCCESS);
        const smartCareUrl = this.context.get('smartCareUrl')
        applicationContext.redirect(ApplicationRoute.CLINICAL_NOTES_CONSENT_TRANSCRIPT, {}, { state: { smartCareUrl } });
      });
  }
}